/**
 * Iter XX.85 — CoachRecommendationCard.
 *
 * A distinct card rendered BENEATH the assistant message when the
 * server has attached a `coach_recommendation` block. It signals
 * to the reader "this is Gully speaking as your coach continuing
 * the conversation", not a system-generated ad.
 *
 * Header: "My recommendation" (deliberate — never "Gully's note"
 * or anything that reads system-generated).
 *
 * Actions:
 *   · CTA — navigate to /plus AND fire clicked event.
 *   · × dismiss — hide locally + fire dismissed event.
 *
 * On mount, fire `shown` once (idempotent via ref) so the analytics
 * pipeline can measure impression → click.
 */
import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { getToken } from "../lib/nativeTokenStorage";
import { credentialsMode } from "../lib/nativePlatform";

const API_BASE = process.env.REACT_APP_BACKEND_URL || "";

async function _track(event, recommendationId) {
  if (!API_BASE || !recommendationId) return;
  try {
    const headers = { "Content-Type": "application/json" };
    try {
      const t = getToken();
      if (t) headers.Authorization = `Bearer ${t}`;
    } catch { /* noop */ }
    await fetch(`${API_BASE}/api/recommendations/track`, {
      method: "POST",
      headers,
      credentials: credentialsMode(),
      body: JSON.stringify({
        recommendation_id: recommendationId,
        event,
      }),
      keepalive: true,
    });
  } catch { /* non-fatal — analytics only */ }
  // Mirror to Clarity so the founder can filter recordings by
  // `plus_recommendation_*` events.
  try {
    if (typeof window !== "undefined" && window.clarity) {
      window.clarity("event", `plus_recommendation_${event}`);
    }
  } catch { /* noop */ }
  // Meta Pixel too — track click as a lead-adjacent event.
  try {
    if (typeof window !== "undefined" && window.fbq && event === "clicked") {
      window.fbq("trackCustom", "PlusRecommendationClicked");
    }
  } catch { /* noop */ }
}

export default function CoachRecommendationCard({ recommendation }) {
  const [dismissed, setDismissed] = useState(false);
  const shownFiredRef = useRef(false);
  const rid = recommendation?.recommendation_id;

  useEffect(() => {
    if (shownFiredRef.current) return;
    if (!rid) return;
    shownFiredRef.current = true;
    _track("shown", rid);
  }, [rid]);

  if (!recommendation || !recommendation.text || dismissed) return null;

  const header = recommendation.header || "My recommendation";
  const ctaLabel = recommendation.cta_label || "Learn more about Gully Plus";
  const ctaUrl = recommendation.cta_url || "/plus";

  return (
    <div
      data-testid="coach-recommendation-card"
      data-reco-id={rid || ""}
      className={
        "mt-3 rounded-2xl border border-emerald-900/40 "
        + "bg-gradient-to-br from-emerald-950/30 to-neutral-900/50 "
        + "p-4 sm:p-5 space-y-3"
      }
    >
      <div className="flex items-start justify-between gap-3">
        <div
          data-testid="coach-recommendation-header"
          className="text-[11px] uppercase tracking-[0.14em] text-emerald-400/80 font-medium"
        >
          {header}
        </div>
        <button
          type="button"
          onClick={() => {
            setDismissed(true);
            _track("dismissed", rid);
          }}
          aria-label="Dismiss recommendation"
          data-testid="coach-recommendation-dismiss"
          className="shrink-0 h-6 w-6 rounded-full text-neutral-500 hover:text-neutral-200 hover:bg-neutral-800 transition-colors flex items-center justify-center text-lg leading-none"
        >
          ×
        </button>
      </div>
      <p
        data-testid="coach-recommendation-text"
        className="text-[14.5px] leading-[1.55] text-neutral-100 whitespace-pre-wrap"
      >
        {recommendation.text}
      </p>
      <div className="pt-1">
        <Link
          to={ctaUrl}
          onClick={() => _track("clicked", rid)}
          data-testid="coach-recommendation-cta"
          className={
            "inline-flex items-center gap-2 h-9 px-4 rounded-full "
            + "bg-emerald-500 hover:bg-emerald-400 "
            + "text-neutral-950 text-[13px] font-medium transition-colors"
          }
        >
          {ctaLabel}
          <span aria-hidden="true">→</span>
        </Link>
      </div>
    </div>
  );
}
